import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Linkedin } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { getInsightBySlug, INSIGHTS } from "@/data/insights";
import { Button } from "@/components/ui/button";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function InsightDetail() {
  const { slug } = useParams();
  const post = getInsightBySlug(slug);

  if (!post) {
    return <Navigate to="/insights" replace />;
  }

  const related = INSIGHTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <article data-testid="page-insight-detail" className="pb-24">
      {/* Hero */}
      <div className="relative border-b border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 pt-14 pb-12">
          <Link
            to="/insights"
            data-testid="insight-back-link"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground group"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Back to insights
          </Link>

          <p className="mt-8 font-mono-ui text-[11px] uppercase tracking-[0.28em] text-accent">
            {post.tag}
          </p>
          <h1 className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-mono-ui text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3 w-3" />
              {formatDate(post.date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              {post.readTime}
            </span>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-6 lg:px-10 pb-12">
          <div className="aspect-[16/8] overflow-hidden border border-border bg-secondary">
            <img
              src={post.hero}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-3xl px-6 lg:px-10 pt-14">
        <div className="prose-vp">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>

        {/* Share */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Enjoyed this? Share it, or subscribe by connecting on LinkedIn.
          </p>
          <a
            data-testid="insight-linkedin-button"
            href="https://www.linkedin.com/in/vicente-palomarez-b89290109/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" size="sm" className="rounded-sm gap-2">
              <Linkedin className="h-4 w-4" />
              Connect on LinkedIn
            </Button>
          </a>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="mx-auto max-w-6xl px-6 lg:px-10 mt-20">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-accent">
            // More reading
          </p>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/insights/${r.slug}`}
                data-testid={`insight-related-${r.slug}`}
                className="group border border-border bg-background overflow-hidden hover:-translate-y-1 transition-transform"
              >
                <div className="aspect-[16/9] overflow-hidden bg-secondary">
                  <img
                    src={r.hero}
                    alt={r.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <p className="font-mono-ui text-[10px] uppercase tracking-widest text-accent">
                    {r.tag}
                  </p>
                  <h3 className="mt-2 font-display font-bold text-lg leading-tight">
                    {r.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
